/**
 * Kundenimport (XLSX) — Jun 2026 (User-Wunsch).
 * Liegt in den Schnittstellen (Einstellungen → Marktplatz → Schnittstellen).
 * Ablauf: Datei wählen → Vorschau (prüfen) → Import starten (Hintergrund-Job
 * mit Fortschritt). Dedupliziert per Kundennummer, übernimmt Fahrzeuge in die
 * Kunden-Garage und Marketing-Einwilligungen je Kanal.
 */
import { useRef, useState } from "react";
import { Users, Upload, CheckCircle2, AlertTriangle, Loader2, FileSpreadsheet, Car, Download } from "lucide-react";
import { Button } from "../ui/button";
import { CollapsibleCard } from "./_shared";
import api from "../../lib/api";
import { toast } from "sonner";

const CONSENT_LABELS = { brief: "Brief", telefon: "Tel.", fax: "Fax", email: "E-Mail", sms: "SMS", mobil: "Mobil" };

export function CustomerImportSection({ currentUser }) {
    void currentUser;
    const fileRef = useRef(null);
    const [files, setFiles] = useState([]);
    const [checking, setChecking] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [preview, setPreview] = useState(null);   // { import_id, summary, sample }
    const [running, setRunning] = useState(false);
    const [status, setStatus] = useState(null);      // session status object
    const [createReminders, setCreateReminders] = useState(false);

    const reset = () => {
        setFiles([]); setPreview(null); setStatus(null);
        if (fileRef.current) fileRef.current.value = "";
    };

    const onPick = (e) => {
        const fs = Array.from(e.target.files || []);
        setFiles(fs); setPreview(null); setStatus(null);
    };

    const doPreview = async () => {
        if (!files.length) { toast.error("Bitte zuerst eine oder mehrere Excel-Dateien wählen"); return; }
        setChecking(true); setPreview(null); setStatus(null);
        try {
            const fd = new FormData();
            files.forEach((f) => fd.append("files", f));
            const { data } = await api.post("/dealer/customers/import/preview", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setPreview(data);
            toast.success(`${data.summary.total} Kunden erkannt`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Datei konnte nicht geprüft werden");
        } finally { setChecking(false); }
    };

    const downloadCleaned = async () => {
        if (!preview?.import_id) return;
        setDownloading(true);
        try {
            const res = await api.get(`/dealer/customers/import/${preview.import_id}/cleaned.xlsx`, { responseType: "blob" });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const a = document.createElement("a");
            a.href = url;
            a.download = "kunden_fahrzeuge_bereinigt.xlsx";
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Bereinigte Datei konnte nicht erstellt werden");
        } finally { setDownloading(false); }
    };

    const pollStatus = async (importId) => {
        try {
            const { data } = await api.get(`/dealer/customers/import/${importId}/status`);
            setStatus(data);
            if (data.status === "done") {
                setRunning(false);
                toast.success(`Import fertig: ${data.created} neu, ${data.updated} aktualisiert, ${data.vehicles_imported} Fahrzeuge`);
                return;
            }
            if (data.status === "error") {
                setRunning(false);
                toast.error(`Import-Fehler: ${data.error || "unbekannt"}`);
                return;
            }
            setTimeout(() => pollStatus(importId), 1200);
        } catch {
            setTimeout(() => pollStatus(importId), 2000);
        }
    };

    const startImport = async () => {
        if (!preview?.import_id) return;
        setRunning(true);
        setStatus({ status: "running", processed: 0, total: preview.summary.total, created: 0, updated: 0, vehicles_imported: 0 });
        try {
            await api.post(`/dealer/customers/import/${preview.import_id}/commit?create_reminders=${createReminders ? "true" : "false"}`);
            pollStatus(preview.import_id);
        } catch (e) {
            setRunning(false);
            toast.error(e?.response?.data?.detail || "Import konnte nicht gestartet werden");
        }
    };

    const s = preview?.summary;
    const done = status?.status === "done";
    const pct = status && status.total ? Math.min(100, Math.round((status.processed / status.total) * 100)) : 0;

    return (
        <CollapsibleCard
            icon={Users}
            iconClass="text-[#7C3AED]"
            title="Kundenimport"
            hint="Kundenstamm aus Excel (.xlsx) importieren — inkl. Fahrzeuge & Marketing-Einwilligungen, mit Dedup."
            testId="customer-import-section"
        >
            {/* Datei wählen */}
            <div className="flex flex-wrap items-center gap-2 mb-4">
                <input
                    ref={fileRef}
                    type="file"
                    accept=".xlsx,.xlsm"
                    multiple
                    onChange={onPick}
                    className="hidden"
                    data-testid="customer-import-file-input"
                />
                <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} className="rounded-sm font-bold border-[#111827]" data-testid="customer-import-pick-btn">
                    <FileSpreadsheet className="w-4 h-4 mr-1.5" /> Datei(en) wählen
                </Button>
                {files.length > 0 && (
                    <span className="text-xs text-gray-700 font-medium truncate max-w-[280px]" data-testid="customer-import-filename">
                        {files.length === 1 ? files[0].name : `${files.length} Dateien gewählt`}
                    </span>
                )}
                <Button type="button" onClick={doPreview} disabled={!files.length || checking} className="rounded-sm font-bold bg-[#111827] hover:bg-black text-white" data-testid="customer-import-preview-btn">
                    {checking ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Upload className="w-4 h-4 mr-1.5" />} Prüfen &amp; zusammenführen
                </Button>
                {(preview || status) && (
                    <Button type="button" variant="ghost" onClick={reset} className="rounded-sm text-xs text-gray-500" data-testid="customer-import-reset-btn">
                        Zurücksetzen
                    </Button>
                )}
            </div>

            {/* Vorschau-Zusammenfassung */}
            {s && (
                <div className="border border-gray-200 rounded-sm overflow-hidden mb-4" data-testid="customer-import-preview">
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-gray-200">
                        <Stat label="Kunden gesamt" value={s.total} testid="ci-total" />
                        <Stat label="Neue Kunden" value={s.new} cls="text-emerald-700" testid="ci-new" />
                        <Stat label="Bereits vorhanden" value={s.update} cls="text-blue-700" testid="ci-update" />
                        <Stat label="Fahrzeuge" value={s.vehicles} cls="text-[#7C3AED]" testid="ci-vehicles" />
                    </div>
                    <div className="px-4 py-2 text-[11px] bg-amber-50 border-t border-amber-100 text-amber-900 flex flex-wrap items-center gap-x-4 gap-y-1" data-testid="ci-dedup-summary">
                        <span>👤 Kunden-Abgleich org-weit per <b>Kd.-Nr. + E-Mail + FIN</b>: {s.new} neu · {s.update} bereits im System</span>
                        {(s.matched_email != null || s.matched_fin != null || s.matched_kdnr != null) && (
                            <span data-testid="ci-match-breakdown">
                                davon erkannt: {s.matched_kdnr ? `${s.matched_kdnr}× Kd.-Nr. · ` : ""}{s.matched_email ?? 0}× E-Mail · <b className="text-purple-700">{s.matched_fin ?? 0}× über Fahrgestellnummer</b>
                            </span>
                        )}
                        <span>🏍️ Fahrzeuge per <b>FIN/Kennzeichen</b> (alle Standorte): {s.vehicles_new ?? s.vehicles} neu · {s.vehicles_existing ?? 0} bereits im System</span>
                    </div>
                    <div className="px-4 py-2 text-[11px] text-gray-500 bg-gray-50 border-t border-gray-200 flex flex-wrap items-center justify-between gap-2">
                        <span>
                            {s.rows_read} Zeilen gelesen{s.files > 1 ? ` aus ${s.files} Dateien` : ""}{s.skipped ? ` · ${s.skipped} übersprungen (zu wenig Daten)` : ""}
                        </span>
                        <Button type="button" variant="outline" size="sm" onClick={downloadCleaned} disabled={downloading} className="rounded-sm text-[11px] font-bold border-[#111827] h-7" data-testid="customer-import-download-btn">
                            {downloading ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Download className="w-3.5 h-3.5 mr-1" />} Bereinigte Liste herunterladen (XLSX)
                        </Button>
                    </div>

                    {/* Stichprobe */}
                    <div className="overflow-x-auto">
                        <table className="w-full text-xs" data-testid="customer-import-sample">
                            <thead className="bg-gray-50 text-gray-500 uppercase text-[10px]">
                                <tr>
                                    <th className="text-left px-3 py-2">Kd-Nr.</th>
                                    <th className="text-left px-3 py-2">Name</th>
                                    <th className="text-left px-3 py-2">Ort</th>
                                    <th className="text-left px-3 py-2">E-Mail</th>
                                    <th className="text-center px-3 py-2">Fzg.</th>
                                    <th className="text-left px-3 py-2">Einwilligung</th>
                                </tr>
                            </thead>
                            <tbody>
                                {preview.sample.map((r, i) => (
                                    <tr key={i} className="border-t border-gray-100">
                                        <td className="px-3 py-1.5 text-gray-500">{r.customer_number || "—"}</td>
                                        <td className="px-3 py-1.5 font-medium text-[#111827]">
                                            {r.name || "—"}
                                            {r.type === "business" && <span className="ml-1 text-[9px] uppercase bg-gray-100 px-1 rounded-sm text-gray-500">Firma</span>}
                                            {r.existing
                                                ? <span className={`ml-1 text-[9px] uppercase px-1 rounded-sm ${r.matched_by === "fin" ? "bg-purple-100 text-purple-700" : "bg-blue-100 text-blue-700"}`} data-testid={`ci-row-existing-${i}`}>
                                                    {r.matched_by === "fin" ? "vorhanden · via FIN" : r.matched_by === "kdnr" ? "vorhanden · via Kd-Nr." : "vorhanden · via E-Mail"}
                                                  </span>
                                                : <span className="ml-1 text-[9px] uppercase bg-emerald-100 text-emerald-700 px-1 rounded-sm" data-testid={`ci-row-new-${i}`}>neu</span>}
                                        </td>
                                        <td className="px-3 py-1.5 text-gray-600">{r.city || "—"}</td>
                                        <td className="px-3 py-1.5 text-gray-600">{r.email || "—"}</td>
                                        <td className="px-3 py-1.5 text-center">{r.vehicles ? <span className="inline-flex items-center gap-0.5 text-[#7C3AED]"><Car className="w-3 h-3" />{r.vehicles}</span> : "—"}</td>
                                        <td className="px-3 py-1.5">
                                            <div className="flex flex-wrap gap-1">
                                                {Object.entries(r.consent || {}).filter(([, v]) => v).map(([k]) => (
                                                    <span key={k} className="text-[9px] uppercase bg-emerald-100 text-emerald-800 px-1 rounded-sm">{CONSENT_LABELS[k] || k}</span>
                                                ))}
                                                {!Object.values(r.consent || {}).some(Boolean) && <span className="text-[10px] text-gray-300">keine</span>}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Fortschritt / Ergebnis */}
            {status && (
                <div className="border border-gray-200 rounded-sm p-4 mb-4" data-testid="customer-import-progress">
                    {!done ? (
                        <>
                            <div className="flex items-center justify-between text-xs mb-2">
                                <span className="font-bold flex items-center gap-1.5"><Loader2 className="w-4 h-4 animate-spin text-[#7C3AED]" /> Import läuft…</span>
                                <span className="text-gray-500">{status.processed} / {status.total}</span>
                            </div>
                            <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                                <div className="h-full bg-[#7C3AED] transition-all" style={{ width: `${pct}%` }} />
                            </div>
                        </>
                    ) : (
                        <div className="flex items-start gap-2 text-sm" data-testid="customer-import-done">
                            <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
                            <div>
                                <p className="font-bold text-[#111827]">Import abgeschlossen</p>
                                <p className="text-xs text-gray-600 mt-0.5">
                                    {status.created} neue Kunden · {status.updated} aktualisiert · {status.vehicles_imported} neue Fahrzeuge in die Garagen übernommen{status.vehicles_updated ? ` · ${status.vehicles_updated} vorhandene Fahrzeuge aktualisiert/zugeordnet` : ""}.
                                </p>
                                {status.reminders_created > 0 && (
                                    <p className="text-xs text-[#7C3AED] font-medium mt-1.5" data-testid="ci-reminders-result">
                                        🔧 {status.reminders_created} Wartungserinnerung(en) erstellt{status.reminders_skipped_no_email ? ` · ${status.reminders_skipped_no_email} ohne E-Mail übersprungen` : ""} —{" "}
                                        <a href="/dealer/invoices?tab=customers" className="underline font-bold" data-testid="ci-reminders-link">
                                            zur Freigabe (Kunden → Service-Erinnerungen)
                                        </a>
                                    </p>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Import starten */}
            {s && !done && (
                <>
                    <label className="flex items-start gap-2 mb-3 text-sm text-[#111827] cursor-pointer bg-[#7C3AED]/5 border border-[#7C3AED]/20 rounded-sm p-3" data-testid="ci-create-reminders-wrap">
                        <input
                            type="checkbox"
                            checked={createReminders}
                            onChange={(e) => setCreateReminders(e.target.checked)}
                            className="w-4 h-4 mt-0.5 accent-[#7C3AED]"
                            data-testid="ci-create-reminders-checkbox"
                        />
                        <span>
                            <span className="font-bold">Wartungs-/Serviceerinnerung für diese Kunden erstellen</span>
                            <span className="block text-[11px] text-gray-500 mt-0.5">
                                Legt je Fahrzeug (mit E-Mail-Adresse) eine Erinnerung in der Warteschlange an — Versand erst nach Ihrer Freigabe unter „Kunden → Service-Erinnerungen".
                            </span>
                        </span>
                    </label>
                    <Button
                        type="button"
                        onClick={startImport}
                        disabled={running}
                        className="w-full bg-[#7C3AED] hover:bg-[#6D28D9] text-white font-bold rounded-sm uppercase tracking-wider disabled:opacity-50"
                        data-testid="customer-import-start-btn"
                    >
                        {running ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin inline" /> Importiert…</> : <>{s.total} Kunden importieren</>}
                    </Button>
                </>
            )}

            <div className="mt-3 flex items-start gap-1.5 text-[11px] text-gray-400">
                <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                Erkannte Spalten: Kundennummer, Anrede, Name/Vorname, Firma, Straße/PLZ/Ort, Telefon(nummer), E-Mail(-Adresse), Geburtstag, Marke, Modell/Modelltext, EZ/Erstzu, Bauart, Fahrzeug-Ident-Nr/Fg-Nr (FIN), Amtl. Kennz. sowie Einwilligungen. Fehlt die Marke, wird sie automatisch aus Modelltext bzw. FIN abgeleitet (Vespa, Piaggio, eSchwalbe …).
            </div>
        </CollapsibleCard>
    );
}

function Stat({ label, value, cls = "text-[#111827]", testid }) {
    return (
        <div className="bg-white px-4 py-3" data-testid={testid}>
            <div className={`text-2xl font-black ${cls}`}>{value ?? 0}</div>
            <div className="text-[10px] uppercase tracking-wider text-gray-400">{label}</div>
        </div>
    );
}
